// Shared component styles. Preserve selector order, style values, and caller overrides.
import { pillButtonInteractiveSx, pillButtonFilledSx } from "./buttonStyles";

export const PURPLE = "#9C4FFF";
export const TEXT = "#344054";
export const MUTED = "#667085";
export const BORDER = "#EAECF0";
export const DISABLED_TEXT = "#B3B8C5";
export const SOFT_PURPLE_HOVER_BG = "#F5F3FF";
export const bulkActionStyles = {
  menuArrow: () => ({
    fontSize: 16
  }),
  menuButton: ({
    unavailable,
    canShowDisabledMessage,
    action
  }) => ({
    minWidth: "auto",
    p: 0,
    px: 0,
    py: 0,
    borderRadius: 999,
    textTransform: "none",
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.2,
    color: unavailable ? DISABLED_TEXT : PURPLE,
    background: "transparent !important",
    backgroundColor: "transparent !important",
    border: "none !important",
    borderColor: "transparent !important",
    boxShadow: "none !important",
    justifyContent: "flex-start",
    cursor: canShowDisabledMessage ? "not-allowed" : undefined,
    "&:hover, &.Mui-focusVisible, &:active": {
      background: `${SOFT_PURPLE_HOVER_BG} !important`,
      backgroundColor: `${SOFT_PURPLE_HOVER_BG} !important`,
      border: "none !important",
      borderColor: "transparent !important",
      boxShadow: "none !important",
      color: unavailable ? DISABLED_TEXT : "#7C3AED"
    },
    "&.Mui-disabled": {
      color: DISABLED_TEXT,
      background: "transparent !important",
      backgroundColor: "transparent !important",
      border: "none !important",
      borderColor: "transparent !important",
      boxShadow: "none !important"
    },
    ...(action.sx || {})
  }),
  actionMenu: ({
    action
  }) => ({
    mt: 0.5,
    minWidth: action.menuWidth || 220,
    borderRadius: 2.5,
    boxShadow: "0 16px 40px rgba(15,23,42,0.16)",
    ...(action.menuPaperSx || {})
  }),
  menuDivider: () => ({
    my: 0.5
  }),
  menuGroup: ({
    index
  }) => ({
    px: 2,
    pt: index === 0 ? 1.25 : 1.5,
    pb: 0.5
  }),
  menuGroupTitle: () => ({
    fontSize: 11,
    fontWeight: 700,
    color: MUTED,
    letterSpacing: 0,
    textTransform: "none"
  }),
  menuOption: ({
    insetLevel,
    action,
    normalizedOption
  }) => ({
    fontSize: 13,
    minHeight: 40,
    pl: 2 + insetLevel * 1.5,
    ...(action.menuItemSx || {}),
    ...(normalizedOption.sx || {})
  }),
  feedbackPopper: () => ({
    zIndex: theme => theme.zIndex.snackbar
  }),
  feedbackAlert: () => ({
    width: "max-content",
    maxWidth: 320,
    boxShadow: "0 14px 32px rgba(15,23,42,0.16)",
    "& .MuiAlert-message": {
      fontSize: 12,
      lineHeight: 1.35
    }
  }),
  actionDivider: ({
    action
  }) => ({
    mx: 0.8,
    my: 0.4,
    borderColor: BORDER,
    ...(action.sx || {})
  }),
  actionWrapper: () => ({
    display: "inline-flex",
    alignItems: "center"
  }),
  actionButton: ({
    disabled,
    action
  }) => ({
    minWidth: "auto",
    p: 0,
    px: 0,
    py: 0,
    borderRadius: 999,
    textTransform: "none",
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.2,
    color: disabled ? "#B3B8C5" : PURPLE,
    background: "transparent !important",
    backgroundColor: "transparent !important",
    border: "none !important",
    borderColor: "transparent !important",
    boxShadow: "none !important",
    justifyContent: "flex-start",
    "&:hover, &.Mui-focusVisible, &:active": {
      background: `${SOFT_PURPLE_HOVER_BG} !important`,
      backgroundColor: `${SOFT_PURPLE_HOVER_BG} !important`,
      border: "none !important",
      borderColor: "transparent !important",
      boxShadow: "none !important",
      color: disabled ? "#B3B8C5" : "#7C3AED"
    },
    "&.Mui-disabled": {
      color: "#B3B8C5",
      background: "transparent !important",
      backgroundColor: "transparent !important",
      border: "none !important",
      borderColor: "transparent !important",
      boxShadow: "none !important"
    },
    ...(action.sx || {})
  }),
  confirmButton: ({
    resolvedTextColor,
    selectionMissing,
    resolvedDisabledBackground,
    resolvedBackground,
    resolvedDisabledBorder,
    buttonConfig
  }) => [pillButtonInteractiveSx, {
    ...pillButtonFilledSx,
    minWidth: "auto",
    height: 40,
    minHeight: 40,
    px: 2.5,
    fontSize: 13,
    fontWeight: 400,
    color: resolvedTextColor,
    backgroundColor: selectionMissing ? resolvedDisabledBackground : resolvedBackground,
    border: `1px solid ${selectionMissing ? resolvedDisabledBorder : resolvedBackground}`,
    whiteSpace: "nowrap",
    borderRadius: 999,
    boxShadow: "none",
    "&:hover": {
      background: "#FFFFFF !important",
      backgroundColor: "#FFFFFF !important",
      color: `${resolvedBackground} !important`,
      borderColor: `${resolvedBackground} !important`,
      boxShadow: "none !important"
    },
    "&:active": {
      background: "#FFFFFF !important",
      backgroundColor: "#FFFFFF !important",
      color: `${resolvedBackground} !important`,
      borderColor: `${resolvedBackground} !important`,
      transform: "translateY(1px)",
      boxShadow: "none !important"
    },
    "&:focus-visible, &.Mui-focusVisible": {
      outline: `3px solid ${buttonConfig.background || PURPLE}`,
      outlineOffset: 2,
      boxShadow: "0 0 0 1px rgba(255,255,255,0.54) inset"
    },
    "&.Mui-disabled": {
      color: resolvedTextColor,
      backgroundColor: resolvedDisabledBackground,
      borderColor: resolvedDisabledBorder,
      boxShadow: "none",
      opacity: typeof buttonConfig.disabledOpacity === "number" ? buttonConfig.disabledOpacity : 0.45
    }
  }, buttonConfig.sx || {}],
  confirmationPopper: () => ({
    zIndex: theme => theme.zIndex.snackbar
  }),
  warningBanner: () => ({
    mb: 1,
    px: 1.5,
    py: 1.2,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1.5,
    flexWrap: "wrap",
    border: "1px solid #FDE68A",
    borderRadius: 2.5,
    backgroundColor: "#FFFBEB",
    boxShadow: "0 10px 24px rgba(15,23,42,0.08)"
  }),
  warningContent: () => ({
    minWidth: 0,
    flex: 1
  }),
  warningTitle: () => ({
    fontSize: 12,
    fontWeight: 700,
    color: "#92400E",
    mb: 0.25,
    whiteSpace: "nowrap"
  }),
  warningMessage: () => ({
    fontSize: 12,
    lineHeight: 1.45,
    color: "#7C2D12"
  }),
  warningActions: () => ({
    display: "flex",
    alignItems: "center",
    gap: 0.8,
    flexShrink: 0
  }),
  cancelWarning: () => ({
    minWidth: "auto",
    px: 1.5,
    py: 0.6,
    fontSize: 12,
    fontWeight: 700,
    textTransform: "none",
    color: "#7C2D12"
  }),
  confirmWarning: () => ({
    minWidth: "auto",
    px: 1.75,
    py: 0.65,
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    textTransform: "none",
    color: "#fff",
    backgroundColor: PURPLE,
    "&:hover": {
      backgroundColor: PURPLE
    }
  }),
  root: ({
    sx
  }) => ({
    width: "100%",
    px: 1.5,
    py: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 1.5,
    flexWrap: "nowrap",
    overflowX: "auto",
    overflowY: "hidden",
    scrollbarWidth: "thin",
    "&::-webkit-scrollbar": {
      height: 8
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#D0D5DD",
      borderRadius: 999
    },
    backgroundColor: "#fff",
    ...sx
  }),
  labelGroup: () => ({
    display: "flex",
    alignItems: "baseline",
    gap: 1,
    flexWrap: "nowrap",
    minWidth: "max-content",
    flexShrink: 0
  }),
  label: () => ({
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.2,
    color: MUTED,
    whiteSpace: "nowrap",
    flexShrink: 0
  }),
  actions: () => ({
    display: "flex",
    alignItems: "baseline",
    gap: 0.8,
    flexWrap: "nowrap",
    minWidth: "max-content"
  }),
  selectedCount: () => ({
    fontSize: 14,
    fontWeight: 500,
    color: TEXT,
    ml: 0.5,
    whiteSpace: "nowrap"
  }),
  rightActions: () => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 1,
    flexWrap: "nowrap",
    flexShrink: 0,
    ml: "auto",
    minWidth: "max-content"
  }),
  exportDialog: () => ({
    width: 840,
    maxWidth: "calc(100vw - 32px)",
    height: "fit-content",
    maxHeight: "calc(100vh - 64px)",
    overflowY: "hidden !important"
  }),
  exportTitle: () => ({
    fontSize: 18,
    fontWeight: 700
  }),
  exportContent: () => ({
    display: "block",
    flex: "none !important",
    overflow: "visible !important",
    minHeight: "0 !important",
    pt: "12px !important",
    pb: 1.5
  }),
  exportDescription: () => ({
    color: MUTED,
    fontSize: 13,
    mb: 1
  }),
  exportProgress: () => ({
    mb: 1.25
  }),
  exportProgressText: () => ({
    fontSize: 12,
    color: MUTED,
    mt: 0.5
  }),
  exportTabs: () => ({
    minHeight: 38,
    mb: 1,
    borderBottom: `1px solid ${BORDER}`,
    "& .MuiTab-root": {
      minHeight: 38,
      minWidth: 0,
      px: 0.75,
      textTransform: "none",
      fontSize: 12.5,
      whiteSpace: "nowrap"
    }
  }),
  exportTableContainer: () => ({
    // Do not reserve a viewport-sized area for a one-row preview.
    // Its height follows the actual rows; only longer selections
    // become scrollable.
    maxHeight: 310,
    overflow: "auto",
    border: `1px solid ${BORDER}`,
    borderRadius: 1,
    flex: "0 0 auto"
  }),
  exportTable: ({
    visibleExportFields
  }) => ({
    minWidth: Math.max(720, visibleExportFields.length * 180),
    tableLayout: "fixed",
    "& .MuiTableCell-root": {
      boxSizing: "border-box",
      lineHeight: 1.35
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      height: 42
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      height: "auto !important"
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      height: 38
    }
  }),
  exportHeaderCell: () => ({
    minWidth: 180,
    py: 0.35,
    px: 1,
    backgroundColor: "#F9FAFB"
  }),
  exportHeaderContent: () => ({
    display: "flex",
    alignItems: "center",
    gap: 0.25,
    minHeight: 30
  }),
  exportCell: () => ({
    minWidth: 180,
    py: 0.55,
    px: 1,
    verticalAlign: "top"
  }),
  exportCellText: () => ({
    color: MUTED,
    fontSize: 12,
    overflow: "hidden",
    textOverflow: "ellipsis"
  }),
  exportEmptyRow: () => ({
    py: 1,
    px: 1.25,
    color: MUTED,
    fontSize: 12
  }),
  exportPagination: () => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 1,
    mt: 1
  }),
  exportPageText: () => ({
    fontSize: 12,
    color: MUTED
  }),
  exportEmptyState: () => ({
    color: MUTED,
    fontSize: 12.5,
    py: 1
  }),
  exportFooter: () => ({
    px: 3,
    pb: 2
  }),
  exportCancel: () => ({
    textTransform: "none"
  }),
  exportDownload: () => ({
    textTransform: "none",
    backgroundColor: PURPLE,
    "&:hover": {
      backgroundColor: PURPLE
    }
  })
};
